import React from "react";
import { Table, Button, Badge } from "reactstrap";
import FooterPagination from "../../Pagination/FooterPagination";
import { useNavigate, useParams } from "react-router-dom";
import { textRoles } from "../../../constants";
import ViewButton from "../../Buttons/ViewButton";
import UserStatusBadge from "../../Badges/UserStatusBadge";
import DropdownMenuActions from "../../Dropdown/DropdownMenuActions";
import { resentInvitation } from "../../../slices/general/thunk";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";

const GATable = ({
  data = [],
  searchParam = "",
  searchDone = false,
  paginationProps = {}
}) => {
  const { id, role } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Ensure data is always an array
  const safeData = Array.isArray(data) ? data : [];

  // Ensure paginationProps has required properties
  const safePaginationProps = {
    totalItems: 0,
    currentPage: 0,
    totalPages: 0,
    pageSize: 10,
    onChangePage: () => { },
    ...paginationProps
  };

  const isNotSearchResult = searchParam && safeData.length === 0 && searchDone;
  const noData = safeData.length === 0 && !searchParam;

  const handleViewProfile = (gaId) => {
    if (!gaId) {
      console.error('GA ID is required for navigation');
      return;
    }
    const route = `/ga/${gaId}`;
    navigate(route);
  };

  const handleRowClick = (userId, event) => {
    if (!userId) {
      console.error('User ID is required for navigation');
      return;
    }

    const selection = window.getSelection()?.toString();
    if (!selection) {
      navigate(`/ga/${userId}`);
    }
  };

  const handleResendInvitation = async (userId) => {
    if (!userId) {
      console.error('User ID is required for resending invitation');
      return;
    }

    try {
      const response = await dispatch(resentInvitation({ userId }));
      const res = response?.payload;

      if (res && !res.error) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Invitation has been resent successfully",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: res?.message || "Invitation could not be resent",
        });
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An unexpected error occurred while resending invitation",
      });
    }
  };

  return (
    <>
      <Table className="table table-hover border table-centered align-middle table-nowrap mb-0">
        <thead className="bg" style={{ background: "var(--thead-bg)" }}>
          <tr>
            <th scope="col">GA Name</th>
            <th scope="col">Contact</th>
            <th scope="col">Number of Brokers</th>
            <th scope="col" className="text-center">
              Status
            </th>
            <th scope="col" className="text-center">
              Manage
            </th>
          </tr>
        </thead>
        <tbody>
          {isNotSearchResult && (
            <tr>
              <td className="text-center" colSpan="5">
                No search results for "{searchParam}"
              </td>
            </tr>
          )}
          {noData ? (
            <tr>
              <td className="text-center" colSpan="5">
                {id && role && textRoles?.[role]
                  ? `${textRoles[role]} has no GA yet.`
                  : "There are no GAs yet."}
              </td>
            </tr>
          ) : (
            safeData.map((ga, index) => (
              <tr
                key={ga?.id || index}
                onClick={(event) => handleRowClick(ga?.id, event)}
                className="cursor-pointer"
              >
                <td>{ga?.companyName || 'N/A'}</td>
                <td>{ga?.email || 'N/A'}</td>
                <td>{ga?.brokersCount ?? 0}</td>
                <td className="text-center fw-normal">
                  <UserStatusBadge
                    status={ga?.firstLogin ? "Active" : "Invited"}
                    color={ga?.firstLogin ? "success" : "warning"}
                  />
                </td>
                <td className="text-center">
                  <DropdownMenuActions
                    onViewProfile={() => handleViewProfile(ga?.id)}
                    onResendInvitation={() => handleResendInvitation(ga?.id)}
                    item={ga}
                    status={ga?.firstLogin ? "Active" : "Invited"}
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {safePaginationProps.totalItems > 0 && (
        <FooterPagination {...safePaginationProps} />
      )}
    </>
  );
};

export default GATable;